import React from "react";

import {
  sizes,
  spinnerVariants,
  variants,
} from "@/components/ui/IconButton/utils";
import { Spinner } from "@/components/ui/Spinner";

import cn from "@/utils/cn";

export type IconButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  size?: keyof typeof sizes;
  variant?: keyof typeof variants;
  isLoading?: boolean;
  icon: React.ReactElement;
};

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      size = "sm",
      variant = "primary",
      icon,
      isLoading = false,
      className = "",
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        className={cn(
          "inline-flex items-center justify-center gap-1.5 rounded-md border border-transparent text-xs font-medium disabled:cursor-not-allowed",
          variants[variant],
          sizes[size],
          { "pointer-events-none": isLoading },
          className,
        )}
        data-testid="icon-button"
        {...props}
      >
        {isLoading && (
          <Spinner size="xs" className={cn(spinnerVariants[variant])} />
        )}
        {!isLoading &&
          icon &&
          React.cloneElement(icon, {
            className: cn("fill-current", icon.props.className),
            "data-testid": "icon-button-icon",
          })}
        <span className="sr-only">{children}</span>
      </button>
    );
  },
);

IconButton.displayName = "IconButton";
